import React, { Component } from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import { EventFieldGroups, EventTypeDto } from 'stores/configuration/events-workflow/event-types-store';
import { EventWizardData } from 'stores/events/creation-wizard-store';
import { Categories } from 'stores/requests/requests-store';
import EventTypeEditor from 'widgets/bussiness/event-type-editor-tw';
import LocationEditor from 'widgets/bussiness/location-editor-tw';
import PriorityEditor from 'widgets/bussiness/priority-editor-tw';
import ProfessionEditor from 'widgets/bussiness/profession-editor-tw';
import { IdentityService } from 'services/identity-service';
import { resolve } from 'inversify.config';
import LocationEditorWithRoleLocations from 'widgets/bussiness/location-editor-with-rolelocations-tw';
import { LocationsStore } from 'stores/configuration/locations/locations-store';
import { connect } from 'redux-scaffolding-ts';
import { Button, Form } from 'semantic-ui-react';

interface BasicStepMobileProps extends WithTranslation {
  wizardData: EventWizardData;
  onValueChanged: (...changes: [string, any][]) => void;
  selectLocation: LocationsStore;
}

interface MobileBasicStepState {
  wizardData: EventWizardData;
  locationsRecived: string[];
  selectedLocation: string;
}

@connect(['selectLocation', LocationsStore])
class MobileBasicStep extends Component<BasicStepMobileProps, MobileBasicStepState> {
  constructor(props) {
    super(props);

    this.state = {
      wizardData: props.wizardData,
      locationsRecived: [],
      selectedLocation: ''
    };
  }

  @resolve(IdentityService)
  private identityService: IdentityService;

  UNSAFE_componentWillReceiveProps(nextProps: BasicStepMobileProps) {
    if (nextProps && nextProps !== this.props) {
      this.setState({
        wizardData: nextProps.wizardData
      });
    }
  }

  componentDidMount() {
    const currentUserInfo = this.identityService.getUserInfo();

    if (IdentityService.isRoleWithLocations(currentUserInfo)) {
      const rolesLocations = currentUserInfo.locationsByRoles[currentUserInfo.activeRole] as string[];

      if (this.identityService.hasRoleMultipleLocations(currentUserInfo)) {
        this.setState({ locationsRecived: rolesLocations });
      } else {
        this.props.selectLocation.getUserById(rolesLocations[0]).then(res => {
          const { wizardData } = this.state;
          wizardData.location = { id: res.id, title: res.location };
          this.setState({ selectedLocation: res.location });
        });
      }
    }
  }

  private onEventTypeChanged = (eventType: EventTypeDto) => {
    let wizardChanges: Partial<EventWizardData> = {};

    wizardChanges.eventType = eventType;
    wizardChanges.eventTitle = '';
    let changes: [keyof EventWizardData, any][] = [
      ['eventType', eventType],
      ['eventTitle', '']
    ];
    const hasReqDetails = (eventType?.requestFieldGroups || '').includes(EventFieldGroups[EventFieldGroups.RequestDetails]);

    this.setState(
      ({ wizardData }) => {
        if (!hasReqDetails) {
          wizardChanges.location = null;
          wizardChanges.priority = null;
          wizardChanges.category = null;
          wizardChanges.profession = null;
          wizardChanges.isMachineRelated = null;
          changes.push(['location', null], ['priority', null], ['category', null], ['profession', null], ['isMachineRelated', null]);
        }
        return { wizardData: { ...wizardData, ...wizardChanges } };
      },
      () => this.props.onValueChanged(...changes)
    );
  };

  private handleValue = (property: keyof EventWizardData, value: any) => {
    let wizardChanges: Partial<EventWizardData> = { [property]: value, eventTitle: '' };
    let changes: [keyof EventWizardData, any][] = [
      [property, value],
      ['eventTitle', '']
    ];

    if (property === 'isMachineRelated' || property === 'category')
      changes.push(
        ['nmrCluster', null],
        ['nmrFunctionalArea', null],
        ['nmrTrainingName', null],
        ['nmrFunctionalSubArea', null],
        ['nmrClusterId', ''],
        ['nMRFunctionalAreaId', ''],
        ['nMRFunctionalSubAreaId', ''],
        ['nMRTrainingNameId', '']
      );

    if (property === 'isMachineRelated' && value) {
      wizardChanges.category = Categories.Technical;
      changes = [...changes, ['category', Categories.Technical]];
    }
    if (property === 'category' && value === Categories.Functional) {
      wizardChanges.isMachineRelated = false;
      changes = [...changes, ['isMachineRelated', false]];
    }
    this.setState(
      ({ wizardData }) => ({ wizardData: { ...wizardData, ...wizardChanges } }),
      () => this.props.onValueChanged(...changes)
    );
  };

  private showReservedRequired() {
    if (
      this.state.wizardData?.eventType?.requestFieldGroups?.includes(EventFieldGroups[EventFieldGroups.RequestDetails]) &&
      this.state.wizardData?.eventType?.name.toLocaleLowerCase().includes('reserved') &&
      this.state.wizardData?.isMachineRelated == null &&
      this.state.wizardData?.category == null
    ) {
      return false;
    }

    return true;
  }

  render() {
    const { t } = this.props as any;
    const { wizardData, locationsRecived } = this.state;
    const currentUser = this.identityService.getUserInfo();
    const isRoleWithLocations = IdentityService.isRoleWithLocations(currentUser);
    const showMultipleLocations = !IdentityService.isRoleWithLocations(currentUser);
    const reservedRequired = this.showReservedRequired();

    return (
      <Form>
        <form className="space-y-6 p-4 sm:p-6">
          {/* Event Type */}
          <div>
            <div className="required field">
              <label className="block text-sm font-medium text-gray-700 mb-1">{t('Event Type')}</label>
              <EventTypeEditor
                nullable
                value={wizardData.eventType as any}
                onChange={c => this.onEventTypeChanged(c?.id ? (c as EventTypeDto) : null)}
                directOnly
                filterDirectEventCreator={true}
              />
            </div>{' '}
          </div>

          {(wizardData?.eventType?.requestFieldGroups || '').includes(EventFieldGroups[EventFieldGroups.RequestDetails]) && (
            <>
              {/* Event Location */}
              <div>
                <div className="required field">
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('Event Location')}</label>
                  {showMultipleLocations && (
                    <LocationEditor
                      nullable
                      value={wizardData.location}
                      onChange={c => this.handleValue('location', c)}
                      showCountry={true}
                    />
                  )}

                  {isRoleWithLocations && this.identityService.hasRoleMultipleLocations(currentUser) && (
                    <LocationEditorWithRoleLocations
                      className="min-w-full"
                      value={wizardData.location ? wizardData.location.id : null}
                      locationsReceived={locationsRecived}
                      onChange={location =>
                        this.handleValue('location', location?.id ? { id: location.id, title: location?.location } : null)
                      }
                      showCountry={true}
                    />
                  )}
                  {isRoleWithLocations && !this.identityService.hasRoleMultipleLocations(currentUser) && (
                    <input type="text" readOnly className="w-full border border-gray-300 rounded p-2" value={this.state.selectedLocation} />
                  )}
                </div>
              </div>

              {/* Priority and Role in 2-column grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{t('Priority')}</label>
                  <PriorityEditor nullable clearable={true} value={wizardData.priority} onChange={c => this.handleValue('priority', c)} />
                </div>
                <div>
                  <div className="required field">
                    <label className={`block text-sm font-medium text-gray-700 mb-1 ${reservedRequired ? 'required' : ''}`}>
                      {t('Role')}
                    </label>
                    <ProfessionEditor nullable value={wizardData.profession} onChange={c => this.handleValue('profession', c)} />
                  </div>
                </div>
              </div>

              {/* Category */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('Category')}</label>
                <Button.Group className="filter-button-group green" style={{ width: 195 }}>
                  <Button
                    className={wizardData.category === Categories.Technical ? 'active gray-border' : 'gray-border'}
                    onClick={() => this.handleValue('category', Categories.Technical)}
                  >
                    {t('Technical')}
                  </Button>
                  <Button
                    className={wizardData.category === Categories.Functional ? 'active gray-border' : 'gray-border'}
                    onClick={() => this.handleValue('category', Categories.Functional)}
                  >
                    {t('Functional')}
                  </Button>
                </Button.Group>
              </div>

              {/* Machine related */}
              <div className="mb-3">
                <label className="block text-sm font-medium text-gray-700 mb-1">{t('Machine related')}</label>
                <Button.Group className="filter-button-group green" style={{ width: 195 }}>
                  <Button
                    className={wizardData.isMachineRelated === true ? 'active gray-border' : 'gray-border'}
                    onClick={() => this.handleValue('isMachineRelated', true)}
                  >
                    {t('Yes')}
                  </Button>
                  <Button
                    className={wizardData.isMachineRelated === false ? 'active gray-border' : 'gray-border'}
                    onClick={() => this.handleValue('isMachineRelated', false)}
                  >
                    {t('No')}
                  </Button>
                </Button.Group>
              </div>
            </>
          )}
        </form>
      </Form>
    );
  }
}

export default withTranslation()(MobileBasicStep);
